import { Player, Score } from '../types';
import { connectionManager } from './supabase/connectionManager';

interface PlayerStats {
  total_birdies: number;
  total_pars: number;
  total_bogeys: number;
  total_strokes: number;
  total_holes: number;
}

function calculatePlayerStats(scores: Score[]): PlayerStats {
  return scores.reduce((stats, score) => {
    // Calculate score relative to par
    const scoreDiff = score.strokes - score.par;
    
    return {
      total_birdies: stats.total_birdies + (scoreDiff < 0 ? 1 : 0),
      total_pars: stats.total_pars + (scoreDiff === 0 ? 1 : 0),
      total_bogeys: stats.total_bogeys + (scoreDiff > 0 ? 1 : 0),
      total_strokes: stats.total_strokes + score.strokes,
      total_holes: stats.total_holes + 1
    };
  }, {
    total_birdies: 0,
    total_pars: 0,
    total_bogeys: 0,
    total_strokes: 0,
    total_holes: 0
  });
}

interface PendingScore {
  playerId: string;
  tournamentId: string;
  scores: Score[];
  timestamp: number;
}

const PENDING_SCORES_KEY = 'hvga_pending_scores';

// Function to get pending scores from localStorage
const getPendingScores = (): PendingScore[] => {
  try {
    const stored = localStorage.getItem(PENDING_SCORES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading pending scores:', error);
    return [];
  }
};

// Function to save pending scores to localStorage
const savePendingScores = (scores: PendingScore[]) => {
  try {
    localStorage.setItem(PENDING_SCORES_KEY, JSON.stringify(scores));
  } catch (error) {
    console.error('Error saving pending scores:', error);
  }
};

// Function to add scores to pending queue
const addToPendingScores = (score: PendingScore) => {
  const pending = getPendingScores();
  // Replace existing pending scores for the same player and tournament
  const filtered = pending.filter(p => 
    !(p.playerId === score.playerId && p.tournamentId === score.tournamentId)
  );
  savePendingScores([...filtered, score]);
};

export const handleScoreSubmit = async (
  scores: Record<string, Score[]>,
  tournament: Tournament,
  players: Player[],
  selectedTee: Tee | null,
  isOnline: boolean
): Promise<void> => {
  try {
    // Always save to local storage first
    const pendingScores: PendingScore[] = Object.entries(scores).flatMap(([playerId, playerScores]) =>
      playerScores.map(score => ({
        playerId,
        tournamentId: tournament.id,
        hole: score.hole,
        strokes: score.strokes,
        timestamp: score.timestamp || Date.now(),
        version: score.version || 0
      }))
    );

    // Save to localStorage regardless of online status
    localStorage.setItem(
      `pending_scores_${tournament.id}`,
      JSON.stringify(pendingScores)
    );

    if (!isOnline) {
      console.log('Offline mode: Scores saved locally');
      return;
    }

    // If online, attempt to sync with server
    const { data: currentScores, error: fetchError } = await supabase
      .from('players')
      .select('id, scores, version')
      .eq('tournament_id', tournament.id);

    if (fetchError) throw fetchError;

    // Create a map of current server scores
    const serverScoreMap = new Map(
      currentScores?.map(p => [p.id, { scores: p.scores, version: p.version }]) || []
    );

    // Prepare updates
    const updates = Object.entries(scores).map(([playerId, playerScores]) => {
      const serverData = serverScoreMap.get(playerId);
      const currentVersion = serverData?.version || 0;
      const currentScores = serverData?.scores || [];

      // Merge server and local scores
      const mergedScores = [...currentScores];
      playerScores.forEach(newScore => {
        const existingIndex = mergedScores.findIndex(s => s.hole === newScore.hole);
        if (existingIndex >= 0) {
          // Only update if new score is more recent
          const existingScore = mergedScores[existingIndex];
          if ((newScore.timestamp || 0) > (existingScore.timestamp || 0)) {
            mergedScores[existingIndex] = newScore;
          }
        } else {
          mergedScores.push(newScore);
        }
      });

      return {
        id: playerId,
        tournament_id: tournament.id,
        scores: mergedScores.sort((a, b) => a.hole - b.hole),
        version: currentVersion + 1
      };
    });

    // Perform updates with optimistic locking
    const { error: updateError } = await supabase
      .from('players')
      .upsert(updates, {
        onConflict: 'id',
        ignoreDuplicates: false
      });

    if (updateError) {
      // If version conflict, retry with latest data
      if (updateError.code === '23514') { // Check your actual error code for version conflict
        console.log('Version conflict detected, retrying with latest data...');
        return handleScoreSubmit(scores, tournament, players, selectedTee, isOnline);
      }
      throw updateError;
    }

    // Clear pending scores from localStorage after successful sync
    localStorage.removeItem(`pending_scores_${tournament.id}`);

  } catch (error) {
    console.error('Error submitting scores:', error);
    throw error;
  }
};

// Add online/offline listener to process pending scores
if (typeof window !== 'undefined') {
  window.addEventListener('online', async () => {
    const pending = getPendingScores();
    if (pending.length === 0) return;

    const supabaseClient = connectionManager.getClient();
    if (!supabaseClient) return;

    const processed: PendingScore[] = [];
    
    for (const item of pending) {
      try {
        await syncScores([{
          id: item.playerId,
          tournamentId: item.tournamentId,
          scores: item.scores
        }]);
        processed.push(item);
      } catch (error) {
        console.error('Failed to process pending score:', error);
      }
    }

    // Remove successfully processed scores from pending queue
    const remaining = pending.filter(p => !processed.some(proc => 
      proc.playerId === p.playerId && 
      proc.tournamentId === p.tournamentId
    ));
    savePendingScores(remaining);
  });
}

export async function verifyScoresWithServer(
  tournamentId: string,
  localScores: Record<string, Score[]>
): Promise<{
  isMatch: boolean;
  discrepancies: Array<{
    playerId: string;
    hole: number;
    localScore: number;
    serverScore: number;
  }>;
}> {
  const supabaseClient = connectionManager.getClient();
  if (!supabaseClient) return { isMatch: true, discrepancies: [] };

  try {
    // Fetch current server state for these players
    const playerIds = Object.keys(localScores);
    const { data: serverPlayers } = await supabaseClient
      .from('players')
      .select('id, scores')
      .in('id', playerIds)
      .eq('tournament_id', tournamentId);

    if (!serverPlayers) return { isMatch: true, discrepancies: [] };

    // Compare local and server scores
    const discrepancies: Array<{
      playerId: string;
      hole: number;
      localScore: number;
      serverScore: number;
    }> = [];

    playerIds.forEach(playerId => {
      const serverPlayer = serverPlayers.find(p => p.id === playerId);
      if (!serverPlayer?.scores) return;

      const localPlayerScores = localScores[playerId] || [];
      const serverPlayerScores = serverPlayer.scores;

      // Check each hole in local scores
      localPlayerScores.forEach(localScore => {
        const serverScore = serverPlayerScores.find(s => s.hole === localScore.hole);
        if (!serverScore || serverScore.strokes !== localScore.strokes) {
          discrepancies.push({
            playerId,
            hole: localScore.hole,
            localScore: localScore.strokes,
            serverScore: serverScore?.strokes || 0
          });
        }
      });

      // Check for holes in server but not in local
      serverPlayerScores.forEach(serverScore => {
        const localScore = localPlayerScores.find(s => s.hole === serverScore.hole);
        if (!localScore) {
          discrepancies.push({
            playerId,
            hole: serverScore.hole,
            localScore: 0,
            serverScore: serverScore.strokes
          });
        }
      });
    });

    return {
      isMatch: discrepancies.length === 0,
      discrepancies
    };
  } catch (error) {
    console.error('Error verifying scores:', error);
    return { isMatch: true, discrepancies: [] }; // Fail safe on error
  }
}

export const syncScores = async (playerId: string, tournamentId: string, localScores: any) => {
  try {
    // First, get the current state from Supabase with a lock
    const { data: serverData, error: fetchError } = await supabase
      .from('players')
      .select('scores, total_score, version')
      .eq('id', playerId)
      .eq('tournament_id', tournamentId)
      .single();

    if (fetchError) throw fetchError;

    // If no server data exists, create new record
    if (!serverData) {
      const { error: insertError } = await supabase
        .from('players')
        .insert({
          id: playerId,
          tournament_id: tournamentId,
          scores: localScores,
          total_score: calculateTotalScore(localScores),
          version: 1
        });

      if (insertError) throw insertError;
      return true;
    }

    const serverVersion = serverData.version || 0;

    // Update with optimistic locking - using last write wins
    const { error: updateError } = await supabase
      .from('players')
      .update({
        scores: localScores,
        total_score: calculateTotalScore(localScores),
        version: serverVersion + 1
      })
      .eq('id', playerId)
      .eq('tournament_id', tournamentId)
      .eq('version', serverVersion); // Optimistic locking

    if (updateError) {
      // If update failed due to version mismatch, retry with latest data
      if (updateError.code === '23514') { // Postgres check constraint violation
        return await syncScores(playerId, tournamentId, localScores);
      }
      throw updateError;
    }

    return true;
  } catch (error) {
    console.error('Score synchronization failed:', error);
    throw error;
  }
};

// Helper function to calculate total score
const calculateTotalScore = (scores: any[]): number => {
  if (!Array.isArray(scores)) return 0;
  return scores.reduce((total, score) => total + (score.strokes || 0), 0);
};